import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../lib/supabaseClient';

const TEST_ROLE_KEY = 'bos_test_role';
const VALID_ROLES = ['owner', 'admin', 'technician'];

const RoleContext = createContext({
  userRole: 'owner',
  businessId: null,
  updateRole: async () => { },
  loading: true,
});

export function RoleProvider({ children }) {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState('owner');
  const [businessId, setBusinessId] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const fetchUserRoleAndBusiness = async () => {
      if (!user?.id) {
        if (active) {
          setUserRole('owner');
          setBusinessId(null);
          setLoading(false);
        }
        return;
      }

      try {
        console.log('[RoleContext] Checking role for user:', user.id);
        
        // 1. Check if user is an accepted member of ANY team (prioritize this)
        const { data: teams } = await supabase
          .from('team_members')
          .select('owner_id, role')
          .eq('user_id', user.id)
          .eq('status', 'accepted')
          .limit(1);

        const teamMember = teams?.[0];
        console.log('[RoleContext] Team member check:', teamMember);

        // 2. Fetch profile role
        const { data: profile } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', user.id)
          .maybeSingle();

        const localOverride = localStorage.getItem(TEST_ROLE_KEY);

        let finalRole = 'owner';
        let finalBusinessId = user.id;

        if (teamMember) {
          // If you're a team member, you ARE that role for that business owner
          finalRole = teamMember.role || 'technician';
          finalBusinessId = teamMember.owner_id;
          console.log('[RoleContext] Team member detected:', { role: finalRole, businessId: finalBusinessId });

          // Clear local override if it conflicts with real team role
          if (localOverride === 'owner') {
            localStorage.removeItem(TEST_ROLE_KEY);
          }
        } else {
          // Use profile role or local override for testing
          finalRole = (localOverride && VALID_ROLES.includes(localOverride))
            ? localOverride
            : (profile?.role || 'owner');
          finalBusinessId = user.id;
          console.log('[RoleContext] Owner/standalone user:', { role: finalRole, businessId: finalBusinessId });
        }

        if (active) {
          setUserRole(finalRole);
          setBusinessId(finalBusinessId);
        }
      } catch (e) {
        console.error('Error in RoleContext:', e);
        if (active) {
          setUserRole('owner');
          setBusinessId(user.id);
        }
      } finally {
        if (active) setLoading(false);
      }
    };

    if (!user?.id) {
      setLoading(false);
      return () => { active = false; };
    }

    setLoading(true);
    fetchUserRoleAndBusiness();

    // Realtime: Listen for profile changes (like role updates)
    const profileSubscription = supabase
      .channel(`profile-${user.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'profiles', filter: `id=eq.${user.id}` },
        () => {
          fetchUserRoleAndBusiness();
        }
      )
      .subscribe();

    // Realtime: Listen for team membership changes (acceptance)
    const teamSubscription = supabase
      .channel(`team-${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'team_members', filter: `user_id=eq.${user.id}` },
        () => {
          fetchUserRoleAndBusiness();
        }
      )
      .subscribe();

    return () => {
      active = false;
      if (profileSubscription) supabase.removeChannel(profileSubscription);
      if (teamSubscription) supabase.removeChannel(teamSubscription);
    };
  }, [user?.id]);

  const updateRole = async (newRole) => {
    if (!user?.id) return;
    const safeRole = VALID_ROLES.includes(newRole) ? newRole : 'owner';
    localStorage.setItem(TEST_ROLE_KEY, safeRole);
    try {
      await supabase.from('profiles').update({ role: safeRole }).eq('id', user.id);
    } catch (e) {
      console.error('Failed to update role:', e);
    }
    setUserRole(safeRole);
    window.location.reload();
  };

  return (
    <RoleContext.Provider value={{ userRole, businessId, updateRole, loading }}>
      {children}
    </RoleContext.Provider>
  );
}

export const useRole = () => useContext(RoleContext);
